/**
 * P-7 日報設定 — 突合ルール (`/settings/daily-report/reconciliation-rules`、§14-2)。
 * 突合ルール (左右AST・許容差・on_ng) の定義に加え、§3-9-a・§14-5-4 の決定どおり
 * 突合NGの定型理由マスタ (daily_report_reconciliation_reasons) の管理もこの画面に統合する
 * (独立タブを作らない)。
 * バックエンド: apps/api/src/routes/daily-reports.ts GET/PUT /daily-reports/config/reconciliation-rules、
 * apps/api/src/routes/daily-report-reconciliation-reasons.ts。
 * 設計: docs/design/v05-daily-report-detail.md §3-9 / §3-9-a / §14-2
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useScopeStore, type NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';
import {
  fetchDailyReportConfigFields, fetchDailyReportReconciliationRules, saveDailyReportReconciliationRule,
  fetchDailyReportReconciliationReasons, createDailyReportReconciliationReason, updateDailyReportReconciliationReason,
  type DailyReportConfigField, type DailyReportReconciliationRule, type DailyReportReconciliationReason,
} from '@/lib/daily-report-api';
import { DrExprEditor, buildDrAst, decomposeDrAst, type DrExpr } from './DailyReportAstEditor';

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';
const numberInputCls = `${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;
const ALL_NODES = '__all__';
/** 一覧のスコープ選択専用の値 (突合ルール自体の node_id='全社' を表す ALL_NODES とは別の意味)。
 *  「テナントの全ルールを横断表示」は all_stores 権限が要る (GET 側 node_id 省略時の分岐)。 */
const VIEW_SCOPE_TENANT_WIDE = '__tenant_wide_view__';

type RuleDraft = {
  id: string | null;
  label: string;
  nodeId: string; // ALL_NODES = テナント全体 (node_id null)
  left: DrExpr;
  right: DrExpr;
  tolerance: string;
  onNg: 'block' | 'warn';
  requireReasonOnNg: boolean;
};

const emptyRuleDraft = (defaultFieldId: string): RuleDraft => ({
  id: null, label: '', nodeId: ALL_NODES,
  left: { kind: 'field', field_definition_id: defaultFieldId },
  right: { kind: 'field', field_definition_id: defaultFieldId },
  tolerance: '0', onNg: 'warn', requireReasonOnNg: true,
});

const ruleDraftFromRule = (r: DailyReportReconciliationRule): RuleDraft | null => {
  const left = decomposeDrAst(r.left_ast);
  const right = decomposeDrAst(r.right_ast);
  if (!left || !right) return null;
  return {
    id: r.rule_id, label: r.label, nodeId: r.node_id ?? ALL_NODES, left, right,
    tolerance: String(r.tolerance), onNg: r.on_ng, requireReasonOnNg: r.require_reason_on_ng,
  };
};

function RulesSection() {
  const qc = useQueryClient();
  const scopeRoots = useScopeStore((s) => s.roots);
  const globalSelectedNodeId = useScopeStore((s) => s.selectedNodeId);
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? scopeRoots), [nodesData, scopeRoots]);
  const nodeNameById = useMemo(() => new Map(flatNodes.map(({ node }) => [node.node_id, node.name])), [flatNodes]);

  const { data: fields = [] } = useQuery<DailyReportConfigField[]>({
    queryKey: ['daily-report-config-fields'], queryFn: fetchDailyReportConfigFields,
  });
  const nonComputedFields = useMemo(() => fields.filter((f) => f.status === 'active' && f.value_kind !== 'computed'), [fields]);
  const lineItemFields = useMemo(() => fields.filter((f) => f.value_kind === 'line_items'), [fields]);

  // ⚠️ platform-reviewerレビューHIGH是正: 以前は常に node_id 無しで GET していたため、バックエンド
  // (routes/daily-reports.ts GET /config/reconciliation-rules の②③) が node_id 省略時に all_stores
  // スコープを要求し、own_store の店長 (R-MANAGER、migration 0151 で view-own/manage-own を実grant
  // 済み) がこのタブを開くと必ず403で自店のルールすら見られなかった (P-1 の resolveViewScopeStoreIds
  // フォールバックと非対称)。⇒ 既定は「対象店舗」を1つ選ぶ表示にし (店舗指定時は当該店舗のルール+
  // 全社ルールをバックエンドが返す、①③)、「全社横断で表示」は all_stores 権限保有者だけが選べる
  // 明示オプションにする。
  const [viewScope, setViewScope] = useState('');
  const effectiveViewScope = viewScope || globalSelectedNodeId || flatNodes[0]?.node.node_id || '';

  const { data: rules = [], isLoading, isError } = useQuery<DailyReportReconciliationRule[]>({
    queryKey: ['daily-report-reconciliation-rules', effectiveViewScope],
    queryFn: () => fetchDailyReportReconciliationRules(effectiveViewScope === VIEW_SCOPE_TENANT_WIDE ? undefined : effectiveViewScope),
    enabled: !!effectiveViewScope,
  });

  const [draft, setDraft] = useState<RuleDraft | null>(null);
  const [unsupportedRuleId, setUnsupportedRuleId] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: (d: RuleDraft) => saveDailyReportReconciliationRule({
      ...(d.id ? { rule_id: d.id } : {}),
      node_id: d.nodeId === ALL_NODES ? null : d.nodeId,
      label: d.label.trim(),
      left_ast: buildDrAst(d.left),
      right_ast: buildDrAst(d.right),
      tolerance: Number(d.tolerance) || 0,
      on_ng: d.onNg,
      require_reason_on_ng: d.requireReasonOnNg,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['daily-report-reconciliation-rules'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const openEdit = (r: DailyReportReconciliationRule) => {
    const d = ruleDraftFromRule(r);
    if (!d) { setUnsupportedRuleId(r.rule_id); return; }
    setDraft(d);
  };

  const canSave = !!draft && draft.label.trim().length > 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-h2">突合ルール</h2>
          <p className="text-sm text-text-sub mt-1">左右の項目・許容差・不一致時の扱いを設定します。</p>
        </div>
        <Button variant="primary" size="sm" leftIcon={<Plus size={14} />}
          disabled={nonComputedFields.length === 0}
          onClick={() => setDraft(emptyRuleDraft(nonComputedFields[0]?.field_definition_id ?? ''))}>
          ルールを追加
        </Button>
      </div>

      <div className="w-full sm:w-72">
        <label className="block text-sm text-text-sub mb-1">表示対象</label>
        <Select value={effectiveViewScope} onValueChange={setViewScope}>
          <SelectTrigger aria-label="表示対象"><SelectValue placeholder="店舗を選択してください" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={VIEW_SCOPE_TENANT_WIDE}>全社横断で表示 (全店舗権限が必要)</SelectItem>
            {flatNodes.map(({ node, depth }) => (
              <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-text-sub mt-1">店舗を選ぶと、その店舗のルールと全社ルールの両方が表示されます。</p>
      </div>

      {isLoading ? (
        <p className="text-sm text-text-sub">読み込み中…</p>
      ) : isError ? (
        <p className="text-sm text-danger-text">
          突合ルールの取得に失敗しました
          {effectiveViewScope === VIEW_SCOPE_TENANT_WIDE ? ' (全社横断の閲覧には全店舗権限が必要です。店舗を選択して表示してください)' : ''}。
        </p>
      ) : rules.length === 0 ? (
        <EmptyState message="突合ルールがまだありません" description="「ルールを追加」から登録してください。" />
      ) : (
        <ul className="space-y-2">
          {rules.map((r) => (
            <li key={r.rule_id} className="flex items-center justify-between gap-2 flex-wrap border border-surface-border rounded-lg bg-white p-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-text-body break-words">
                  {r.label}
                  <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border align-middle">
                    {r.on_ng === 'block' ? '提出をブロック' : '警告のみ'}
                  </span>
                </p>
                <p className="text-xs text-text-sub mt-0.5">
                  対象: {r.node_id ? (nodeNameById.get(r.node_id) ?? r.node_id) : '全社'} ／ 許容差: {r.tolerance}
                </p>
              </div>
              <Button variant="ghost" size="sm" leftIcon={<Pencil size={14} />} onClick={() => openEdit(r)}>編集</Button>
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!draft} title={draft?.id ? 'ルールを編集' : 'ルールを追加'} onClose={() => setDraft(null)} size="lg"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-text-sub mb-1">ラベル <span className="text-text-sub">*</span></label>
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={200} className={inputCls} placeholder="例: 売上=ジャーナル合計" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">対象店舗</label>
              <Select value={draft.nodeId} onValueChange={(v) => setDraft({ ...draft, nodeId: v })}>
                <SelectTrigger aria-label="対象店舗"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_NODES}>全社</SelectItem>
                  {flatNodes.map(({ node, depth }) => (
                    <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DrExprEditor expr={draft.left} onChange={(v) => setDraft({ ...draft, left: v })} fields={nonComputedFields} lineItemFields={lineItemFields} />
            <p className="text-center text-xs text-text-sub">対 (差額 = 左辺 − 右辺)</p>
            <DrExprEditor expr={draft.right} onChange={(v) => setDraft({ ...draft, right: v })} fields={nonComputedFields} lineItemFields={lineItemFields} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-text-sub mb-1">許容差</label>
                <input type="number" min={0} step="any" value={draft.tolerance} onChange={(e) => setDraft({ ...draft, tolerance: e.target.value })} className={numberInputCls} />
              </div>
              <div>
                <label className="block text-sm text-text-sub mb-1">不一致時の扱い</label>
                <Select value={draft.onNg} onValueChange={(v) => setDraft({ ...draft, onNg: v as 'block' | 'warn' })}>
                  <SelectTrigger aria-label="不一致時の扱い"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="warn">警告のみ (提出は可能)</SelectItem>
                    <SelectItem value="block">提出をブロック</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-text-body min-h-[44px]">
              <input type="checkbox" checked={draft.requireReasonOnNg} onChange={(e) => setDraft({ ...draft, requireReasonOnNg: e.target.checked })} className="w-4 h-4" />
              不一致を受け入れる際に理由の入力を必須にする
            </label>
          </div>
        )}
      </Modal>

      <Modal open={!!unsupportedRuleId} title="この画面では編集できません" onClose={() => setUnsupportedRuleId(null)} size="sm"
        footer={<Button variant="secondary" onClick={() => setUnsupportedRuleId(null)}>閉じる</Button>}>
        <p className="text-sm text-text-body">
          このルールの左辺・右辺は本画面が対応する構成 (単一項目、または2項の四則演算) を超えています。API経由で編集してください。
        </p>
      </Modal>
    </div>
  );
}

type ReasonDraft = { id: string | null; code: string; label: string; sortOrder: string; isActive: boolean };
const emptyReasonDraft = (sortOrder: number): ReasonDraft => ({ id: null, code: '', label: '', sortOrder: String(sortOrder), isActive: true });

function ReasonsSection() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<ReasonDraft | null>(null);

  const { data: reasons = [], isLoading, isError } = useQuery<DailyReportReconciliationReason[]>({
    queryKey: ['daily-report-reconciliation-reasons'], queryFn: fetchDailyReportReconciliationReasons,
  });

  const saveMut = useMutation({
    mutationFn: (d: ReasonDraft) => {
      const body = { code: d.code.trim(), label: d.label.trim(), sort_order: Number(d.sortOrder) || 0, is_active: d.isActive };
      return d.id ? updateDailyReportReconciliationReason(d.id, body) : createDailyReportReconciliationReason(body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['daily-report-reconciliation-reasons'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const canSave = !!draft && draft.code.trim().length > 0 && /^[a-z0-9_]+$/.test(draft.code.trim()) && draft.label.trim().length > 0;

  return (
    <div className="space-y-3 border-t border-surface-line pt-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-h2">突合NGの定型理由</h2>
          <p className="text-sm text-text-sub mt-1">不一致を受け入れる際に選べる定型理由を管理します (§3-9-a)。</p>
        </div>
        <Button variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft(emptyReasonDraft(reasons.length))}>理由を追加</Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-text-sub">読み込み中…</p>
      ) : isError ? (
        <p className="text-sm text-danger-text">定型理由の取得に失敗しました。</p>
      ) : reasons.length === 0 ? (
        <EmptyState message="定型理由がまだありません" description="「理由を追加」から登録してください。" />
      ) : (
        <ul className="space-y-1.5">
          {reasons.map((r) => (
            <li key={r.reason_id} className="flex items-center justify-between gap-2 flex-wrap border border-surface-border rounded-lg bg-white px-3 py-2">
              <div className="min-w-0">
                <span className="text-sm text-text-body break-words">{r.label}</span>
                <span className="ml-2 text-[11px] text-text-sub">code: {r.code} ／ 並び順: {r.sort_order}</span>
                {!r.is_active && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border align-middle">無効</span>}
              </div>
              <Button variant="ghost" size="sm" leftIcon={<Pencil size={14} />}
                onClick={() => setDraft({ id: r.reason_id, code: r.code, label: r.label, sortOrder: String(r.sort_order), isActive: r.is_active })}>編集</Button>
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!draft} title={draft?.id ? '定型理由を編集' : '定型理由を追加'} onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">コード (半角英小文字・数字・アンダースコア)</label>
              <input value={draft.code} disabled={!!draft.id} onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                maxLength={100} className={`${inputCls} disabled:opacity-60`} placeholder="例: pos_timing_diff" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">表示ラベル</label>
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={200} className={inputCls} placeholder="例: レジ締めタイミングのズレ" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">並び順</label>
              <input type="number" min={0} max={99999} value={draft.sortOrder} onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })} className={numberInputCls} />
            </div>
            {draft.id && (
              <label className="flex items-center gap-2 text-sm text-text-body min-h-[44px]">
                <input type="checkbox" checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} className="w-4 h-4" />
                有効
              </label>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export function DailyReportReconciliationRulesSettingsPage() {
  return (
    <div className="space-y-6">
      <RulesSection />
      <ReasonsSection />
    </div>
  );
}
